import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { Customer, LedgerAccount, Meter, MyTenant, Page, Plan, Subscription } from './types';

/** Тенанты, доступные вошедшему. Кэшируется на всё приложение — меняется редко. */
export const useMyTenants = () =>
  useQuery({ queryKey: ['my-tenants'], queryFn: () => api.get<MyTenant[]>('/my-tenants') });

/**
 * Справочники тенанта одним хуком: абоненты, счётчики и планы нужны почти на каждом экране, чтобы
 * показывать имена вместо идентификаторов. Тянуть их порознь в каждом компоненте значило бы три
 * запроса на экран.
 */
export function useTenantCatalog(tenantId: string) {
  const customers = useQuery({
    queryKey: ['customers', tenantId],
    queryFn: () => api.get<Page<Customer>>(`/tenants/${tenantId}/customers?size=500`),
  });
  const meters = useQuery({
    queryKey: ['meters', tenantId],
    queryFn: () => api.get<Meter[]>(`/tenants/${tenantId}/meters`),
  });
  const plans = useQuery({
    queryKey: ['plans', tenantId],
    queryFn: () => api.get<Plan[]>(`/tenants/${tenantId}/plans`),
  });

  const byId = <T extends { id: string }>(rows: T[] | undefined) =>
    new Map((rows ?? []).map((row) => [row.id, row]));

  return {
    customers: customers.data?.content ?? [],
    meters: meters.data ?? [],
    plans: plans.data ?? [],
    customerById: byId(customers.data?.content),
    meterById: byId(meters.data),
    planById: byId(plans.data),
    isLoading: customers.isLoading || meters.isLoading || plans.isLoading,
  };
}

export const useSubscriptions = (tenantId: string) =>
  useQuery({
    queryKey: ['subscriptions', tenantId],
    queryFn: () => api.get<Page<Subscription>>(`/tenants/${tenantId}/subscriptions?size=500`),
  });

export const useLedger = (tenantId: string) =>
  useQuery({
    queryKey: ['ledger', tenantId],
    queryFn: () => api.get<LedgerAccount[]>(`/tenants/${tenantId}/ledger/accounts`),
  });

/** Копейки → рубли с разделителями, единообразно во всём приложении. */
export const money = (kopecks: number): string =>
  (kopecks / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Числовые количества: целые без хвоста, дробные — до трёх знаков. */
export const qty = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/0+$/, '');

export const date = (iso: string): string => new Date(iso).toLocaleDateString('ru-RU');

/**
 * Цена за единицу для строки счёта — но только когда она честно объясняет сумму. У пакетов и
 * ступеней в этом поле лежит цена пакета или ноль, и показывать её рядом с объёмом значит
 * предлагать читателю умножение, которое не сойдётся. В таких строках цена скрывается.
 */
export const unitPrice = (line: { quantity: number; unitAmountKopecks: number; amountKopecks: number }): string =>
  line.unitAmountKopecks > 0 && Math.round(line.quantity * line.unitAmountKopecks) === line.amountKopecks
    ? money(line.unitAmountKopecks)
    : '—';